import { useForm } from "react-hook-form";
import { Button, Input, Modal, Textarea } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { AdminOrderDetailDTO } from "@wmart/shared-types";

interface FulfilFormValues {
  tracking_number: string;
  notes: string;
}

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const TRACKING_MAX = 120;
const NOTES_MAX = 500;

/**
 * Mark an order as fulfilled. POST /v1/admin/orders/:id/fulfil — both
 * tracking_number and notes are optional per `FulfilOrderBody`. The 409
 * `order.invalid_state_for_fulfil` surfaces via the toast provider when
 * the order is not in `paid`.
 *
 * Rendered inside `<Modal>` (Plan 11 T11) — ESC + click-outside +
 * focus-trap + scroll-lock all come from the primitive.
 */
export function FulfilOrderModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FulfilFormValues>({
    defaultValues: { tracking_number: "", notes: "" },
  });

  const notes = watch("notes");

  async function onSubmit(values: FulfilFormValues) {
    const body: { tracking_number?: string; notes?: string } = {};
    const trimmedTracking = values.tracking_number.trim();
    const trimmedNotes = values.notes.trim();
    if (trimmedTracking) body.tracking_number = trimmedTracking;
    if (trimmedNotes) body.notes = trimmedNotes;
    try {
      await api.post(`/v1/admin/orders/${order.id}/fulfil`, body);
      notify("success", "Order fulfilled");
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Fulfil failed");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Mark as fulfilled"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Mark fulfilled
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <Input
          label="Tracking number"
          placeholder="e.g. TRK123ABC"
          maxLength={TRACKING_MAX}
          helper="Optional. Leave blank for counter handover."
          error={errors.tracking_number?.message}
          {...register("tracking_number", {
            maxLength: {
              value: TRACKING_MAX,
              message: `Max ${TRACKING_MAX} characters`,
            },
          })}
        />

        <Textarea
          label="Notes"
          rows={3}
          maxLength={NOTES_MAX}
          helper={`${notes.length}/${NOTES_MAX}`}
          error={errors.notes?.message}
          {...register("notes", {
            maxLength: {
              value: NOTES_MAX,
              message: `Max ${NOTES_MAX} characters`,
            },
          })}
        />

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
